import styled, { css } from "styled-components"
import { Link } from "react-router-dom"
import { ReactComponent as logo } from "images/brink-logo-icon.svg"

type StyledProps = {
  $expanded?: boolean
  $active?: boolean
  $subMenuExpanded?: boolean
  $collapsed?: boolean
}

export const Wrapper = styled.div<StyledProps>`
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  z-index: 1000;
  transition: transform 0.3s ease;
  transform: translateX(${(p) => (p.$expanded ? "0" : "-26rem")});

  ${(p) => p.theme.mQ.MEDIA_MIN_LARGE} {
    transform: translateX(0);
  }
`

export const Container = styled.div<StyledProps>`
  position: relative;
  width: ${(p) => (p.$collapsed ? "7.2rem" : "26rem")};
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background-color: #0C2937;
  padding: 1.8rem 0;
  z-index: 10002;
  transition: width 0.3s ease;

  hr {
    width: calc(100% - 3rem);
    margin: 1.5rem auto;
    border-bottom: 0.1rem solid rgba(255, 255, 255, 0.1);
    display: ${(p) => (p.$collapsed ? "none" : "block")};
  }
`

export const Logo = styled(logo)<{ $isTestEnvironment: boolean | undefined }>`
  width: 3rem;
  height: 3rem;
  cursor: pointer;
  margin-left: 1.6rem;
  margin-right: ${(p) => (p.$isTestEnvironment ? "1rem" : "1.2rem")};
  flex-shrink: 0;
  fill: ${(p) => p.theme.colors.white};
`

export const Test = styled.p`
  background: ${(p) => p.theme.colors.yellow};
  color: ${(p) => p.theme.colors.black};
  position: absolute;
  width: 100%;
  top: 4.6rem;
  display: flex;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
  text-transform: uppercase;
  padding: 0.1rem;
  line-height: 1.8rem;
`

export const Navigation = styled.nav<StyledProps>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  min-height: 0;
  width: 100%;
  padding: 0 0.6rem;
  overflow-y: auto;
  overflow-x: hidden;
`

export const NavItem = styled.div<StyledProps>`
  width: 100%;
  margin-bottom: 0.5rem;

  > a,
  > p,
  > div {
    color: ${(p) => (p.$active ? "#ffffff" : "#b0b0b0")};
    background: ${(p) => (p.$active ? "rgba(71, 224, 194, 0.15)" : "transparent")};
    border: ${(p) => (p.$active ? "1px solid rgba(71, 224, 194, 0.3)" : "1px solid transparent")};
    transition: all 0.2s;
    font-size: 1.4rem;
    font-weight: 700;
    padding: 0.7rem 1rem;

    svg {
      width: 2rem;
      height: 2rem;
      transition: all 0.2s;
      fill: #000000;
    }
  }
`

export const IconWrapper = styled.span<{ $color?: string }>`
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1.2rem;
  background: ${(p) => p.$color || '#4a90e2'};
  flex-shrink: 0;
`

export const NavLinkStyles = css<StyledProps>`
    text-decoration: none;
    font-size: 1.4rem;
    letter-spacing: 0.05rem;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    margin: 0;
    width: ${(p) => (p.$collapsed ? "6rem" : "100%")};
    padding: 1.2rem 0;
    background: transparent;
    transition: all 0.2s;
    border-radius: 1rem;

    &:hover {
      color: #ffffff;

      svg {
        fill: #ffffff;
      }
    }
  `

export const NavLink = styled(Link)<StyledProps>`
  ${NavLinkStyles}
    cursor: pointer;
    background-color: ${(p) => {
    if (p.$active) return "rgba(71, 224, 194, 0.15)";
    if (p.$subMenuExpanded) return "rgba(255, 255, 255, 0.08)";
    return "transparent";
  }};
    border: ${(p) =>
    p.$active ? "1px solid rgba(71, 224, 194, 0.3)" : "1px solid transparent"
  };

    &:hover {
      color: #ffffff;
      background-color: ${(p) =>
    p.$active
      ? "rgba(71, 224, 194, 0.2)"
      : "rgba(255, 255, 255, 0.08)"
  };

      svg {
        fill: #ffffff;
      }
    }
`

export const NavNoLink = styled.div<StyledProps>`
  ${NavLinkStyles};
  cursor: pointer;
  background-color: ${(p) => {
    if (p.$active) return "rgba(71, 224, 194, 0.15)";
    if (p.$subMenuExpanded) return "rgba(255, 255, 255, 0.08)";
    return "transparent";
  }};
  border: ${(p) =>
    p.$active ? "1px solid rgba(71, 224, 194, 0.3)" : "1px solid transparent"
  };

  &:hover {
    color: #ffffff;
    background-color: ${(p) =>
      p.$active
        ? "rgba(71, 224, 194, 0.2)"
        : "rgba(255, 255, 255, 0.08)"
    };

    svg {
      fill: #ffffff;
    }
  }
`

export const InlineSubMenu = styled.div`
  width: 100%;
  padding-left: 2rem;
  margin-bottom: 1rem;
  background: rgba(255, 255, 255, 0.02);
  border-left: 2px solid rgba(71, 224, 194, 0.3);
  border-radius: 0 0.8rem 0.8rem 0;
`

const InlineSubNavLinkStyles = css<StyledProps>`
  text-decoration: none;
  font-size: 1.3rem;
  letter-spacing: 0.05rem;
  font-weight: 400;
  color: ${(p) => (p.$active ? "#ffffff" : "#b0b0b0")};
  display: flex;
  align-items: center;
  padding: 0.8rem 1.5rem;
  width: 100%;
  background: ${(p) => (p.$active ? "rgba(71, 224, 194, 0.15)" : "transparent")};
  border: ${(p) => (p.$active ? "1px solid rgba(71, 224, 194, 0.3)" : "1px solid transparent")};
  border-radius: 0.6rem;
  transition: all 0.2s;
  margin-bottom: 0.3rem;

  &:hover {
    color: #ffffff;
    background: ${(p) =>
      p.$active
        ? "rgba(71, 224, 194, 0.2)"
        : "rgba(255, 255, 255, 0.05)"
    };
  }
`

export const InlineSubNavLink = styled(Link)<StyledProps>`
  ${InlineSubNavLinkStyles}
`

export const InlineSubNavExternalLink = styled.a<StyledProps>`
  ${InlineSubNavLinkStyles}

  svg {
    fill: currentColor;
    width: 1.2rem;
    height: 1.2rem;
    margin-left: 0.6rem;
  }
`

export const InlineSectionTitle = styled.h4`
  color: ${(p) => p.theme.colors.white};
  font-size: 1.1rem;
  font-weight: 600;
  opacity: 0.7;
  text-transform: uppercase;
  letter-spacing: 0.1rem;
  padding: 1rem 1.5rem 0.5rem 1.5rem;
  margin: 0;

  &:not(:first-child) {
    margin-top: 1rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    padding-top: 1.5rem;
  }
`

export const User = styled(Link)<StyledProps>`
  padding: 0.5rem 0;
  margin: 0 1.6rem;
  color: ${(p) => (p.$active ? "#ffffff" : "#000000")};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  font-size: 1.4rem;
  font-weight: 700;
  width: ${(p) => (p.$collapsed ? "6.8rem" : "auto")};
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;

  svg {
    width: 2rem;
    height: 2rem;
    fill: #000000;
    transition: fill 0.2s;
  }

  &:hover {
    color: #ffffff;

    svg {
      fill: #ffffff;
    }
  }
`

export const SidebarHeader = styled.div<StyledProps>`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  padding: 0 0.8rem;
  margin-bottom: 2rem;
`

export const Title = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
`

export const BrandTitle = styled.h1<StyledProps>`
  color: #ffffff;
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0;
  display: ${(p) => (p.$collapsed ? "none" : "block")};
`

export const CollapseButton = styled.button<StyledProps>`
  background: none;
  border: none;
  color: #b0b0b0;
  cursor: pointer;
  padding: 0.8rem;
  border-radius: 0.8rem;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: ${(p) => (p.$collapsed ? "rotate(180deg)" : "rotate(0deg)")};
  margin: 0 ${(p) => (p.$collapsed ? "auto" : "0 2rem 0 auto")};
  align-self: ${(p) => (p.$collapsed ? "center" : "flex-end")};

  svg {
    width: 1.8rem;
    height: 1.8rem;
    fill: currentColor;
    transition: transform 0.2s;
  }

  &:hover {
    color: #ffffff;
  }
`

export const NavTitle = styled.div<StyledProps>`
  display: ${(p) => (p.$collapsed ? "none" : "flex")};
  flex-direction: column;
  align-items: flex-start;
  opacity: ${(p) => (p.$collapsed ? 0 : 1)};
  transition: opacity 0.2s;
`

export const NavMainText = styled.span`
  font-size: 1.4rem;
  font-weight: 900;
  color: #ffffff;
  line-height: 1.2;
`

export const NavSubText = styled.span`
  font-size: 1.1rem;
  font-weight: 400;
  color: #ffffff;
  line-height: 1.2;
  margin-top: 0.2rem;
  white-space: nowrap;
  opacity: 0.8;
`

export const SidebarClickOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -1;
  cursor: pointer;
`

export const SidebarContent = styled.div<StyledProps>`
  width: ${(p) => (p.$collapsed ? "7.2rem" : "26rem")};
  z-index: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
`

export const HoverTooltip = styled.div<{ $visible: boolean; $top: number }>`
  position: fixed;
  left: 0rem;
  top: ${(p) => p.$top}px;
  height: 6rem;
  width: 20rem;
  background: #0C2937;
  padding: 1.2rem 1.5rem;
  z-index: 10003;
  transform: translateX(${(p) => (p.$visible ? '7.2rem' : '0rem')});
  transform-origin: left center;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  overflow: hidden;
`

export const TooltipContent = styled.div<{ $visible: boolean }>`
  display: flex;
  flex-direction: column;
  min-width: 0;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transform: translateX(${(p) => (p.$visible ? '0' : '-2rem')});
  transition: opacity 0.25s ease ${(p) => (p.$visible ? '0.1s' : '0s')}, transform 0.25s ease ${(p) => (p.$visible ? '0.1s' : '0s')};
`

export const TooltipTitle = styled.div`
  color: #ffffff;
  font-size: 1.4rem;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
`

export const TooltipSubtitle = styled.div`
  color: #ffffff;
  font-size: 1.1rem;
  font-weight: 400;
  opacity: 0.8;
  margin-top: 0.2rem;
  line-height: 1.2;
  white-space: nowrap;
`
